import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Trophy } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { sanitizeApiErrorMessage } from "@/shared/lib/apiClient";
import { DashboardPageHeader } from "@/shared/ui/DashboardPageHeader";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import {
  fetchCompanyDashboardVisitsDetail,
  type VisitRankingFull,
} from "../api/companyApi";
import {
  enterpriseDashboardPathWithPeriod,
  parseDashboardPeriodSearchParams,
} from "../lib/dashboardPeriodQuery";

const COLUMN_KEYS = ["agents", "spouses", "children"] as const;

export function EnterpriseDashboardVisitsDetailPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.dashboard.enterpriseAnalytics.visits;
  const boardLabels = t.safecheck.dashboard.enterpriseBoard.visitDistribution;
  const [searchParams] = useSearchParams();
  const filters = useMemo(
    () => parseDashboardPeriodSearchParams(searchParams),
    [searchParams],
  );
  const backHref = enterpriseDashboardPathWithPeriod(filters);

  const [periodLabel, setPeriodLabel] = useState("");
  const [data, setData] = useState<VisitRankingFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void fetchCompanyDashboardVisitsDetail({
      date_start: filters.dateFrom || undefined,
      date_end: filters.dateTo || undefined,
      hospital_id: filters.hospitalId ? Number(filters.hospitalId) : undefined,
    })
      .then((response) => {
        if (cancelled) return;
        setPeriodLabel(response.period_label);
        setData(response.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          sanitizeApiErrorMessage(
            err instanceof Error ? err.message : "Impossible de charger le détail.",
          ),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.dateFrom, filters.dateTo, filters.hospitalId]);

  const columnLabels: Record<(typeof COLUMN_KEYS)[number], string> = {
    agents: boardLabels.agents,
    spouses: boardLabels.spouses,
    children: boardLabels.children,
  };

  return (
    <div className="space-y-5">
      <Link
        to={backHref}
        className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={16} />
        {labels.back}
      </Link>

      <DashboardPageHeader
        title={labels.title}
        subtitle={periodLabel || labels.subtitleFallback}
        icon={Trophy}
      />

      {error ? (
        <p className="rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted">{labels.loading}</p>
      ) : (
        <div className="theme-card p-5">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {COLUMN_KEYS.map((key) => {
              const rows = data?.[key] ?? [];
              return (
                <div key={key}>
                  <p className="mb-3 border-b border-border pb-2 text-sm font-semibold text-foreground">
                    {columnLabels[key]}
                  </p>
                  {rows.length === 0 ? (
                    <p className="text-sm text-muted">{labels.empty}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[220px] text-sm">
                        <thead>
                          <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted">
                            <th className="w-10 py-2">#</th>
                            <th className="py-2">{labels.beneficiary}</th>
                            <th className="py-2 text-right">{labels.visits}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, index) => (
                            <tr key={`${row.name}-${index}`} className="border-b border-border last:border-0">
                              <td className="py-2 text-muted">{index + 1}</td>
                              <td className="py-2 font-medium text-foreground">{row.name}</td>
                              <td className="py-2 text-right font-semibold tabular-nums">{row.cnt}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
